import { Content, Main, SubContent } from "../../../components/Layout";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import useWrapper from "../../../hooks/useWrapper";
import usePermission from "../../../hooks/usePermission";
import ModalFinancialReceipt from "../../../components/Modal/modal-financial-receipt";
import ModalAdditionalItem from "../../../components/Modal/modal-additional-item";
import ModalAdditionalInvite from "../../../components/Modal/modal-additional-invite";
import ModalDiscountRemove from "../../../components/Modal/modal-discount-remove";
import { traineeFinancial } from "../../../hooks/api/listFinancial";
import { toast } from "react-toastify";
import { Row, Col } from "react-bootstrap";
import TraineeSubMenu from "../../../components/Submenu/TraineeSubMenu";
import TraineeEndBond from "../../../components/Submenu/TraineeEndBond";
import Button from "../../../components/Button";
import Loading from "../../../components/Loading";
import SlipsTable from "../../../components/SlipsTable";
import useFormat from "../../../hooks/utils/useFormat";
import api from "../../../services/api";
import SwitchCheckbox from "../../../components/SwitchCheckBox";
import { formatPaymentSlips } from "../../../hooks/api/listFinancial";
import TableResponsive from "../../../components/TableResponsive";
import StatusFlag from "../../../components/StatusFlag";
import SumBoxes from "../../../components/SumBoxes";
import Results from "../../../components/Results";

export default function TraineesFinancial() {
  const { me, config } = useWrapper();
  const canNegotiate = usePermission("financialGroup", "create");
  const canReadFinancial = usePermission("financialGroup", "reading");
  // remover bônus é um PUT -> mesma permissão que o backend exige, para não
  // existir botão habilitado que falha com 401
  const canUpdateFinancial = usePermission("financialGroup", "update");
  const { trainee, setisWaitingResponse, reloadTraineeData } = useOutletContext() || {};
  const hasFullBond =
    !trainee?.cancel && trainee?.access_status !== "limitado" && trainee?.access_status !== "encerrado";
  // access_status "limitado" = distrato registrado com multa ainda em aberto (o
  // "Encerrado - Multa em Aberto" do DistratoService) — o formando não tem mais
  // vínculo pleno, mas o distrato ainda está em curso. Diferente de "encerrado",
  // que é o distrato já quitado/quitado sem multa.
  const hasActiveDistrato = !!trainee?.cancel && trainee?.access_status === "limitado";
  const { formatReal, formatDate } = useFormat();

  const [reloadFinancial, setreloadFinancial] = useState();
  const reloadFinancialData = () => {
    setreloadFinancial(JSON.stringify(new Date()));
  };

  const [financial, setfinancial] = useState();
  const [totalGenerated, settotalGenerated] = useState();

  // Retorna os totais recém-buscados além de setar o estado: quem encadeia uma
  // ação com o modal de "Gerar Diferença" precisa do valor na hora, e o estado
  // só existiria no render seguinte.
  const fetchFinancialData = async () => {
    try {
      setisLoadingSlips(true);
      const _financial = await traineeFinancial({
        config,
        me,
        trainee_id: trainee?.id,
        commercial_id: trainee?.commercial_id,
      });
      const _total_generated = await api.get(`/total-amount-generated-per-traineer/${trainee?.id}`, config);
      setfinancial(_financial);
      settotalGenerated(_total_generated.data);
      return _total_generated.data;
    } catch (__error) {
      console.log("__error", __error);
      toast.error("Problema ao carregar os pagamentos");
      return null;
    } finally {
      setisLoadingSlips(false);
    }
  };

  useEffect(() => {
    fetchFinancialData();
    // config/me/trainee are intentionally excluded: this fetch should only re-run when explicitly
    // triggered via reloadFinancialData (reloadFinancial), not on every unrelated context/trainee
    // object identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFinancial]);

  // Chamado após adicionar item adicional ou remover bônus — as duas ações
  // AUMENTAM o valor devido. Abre o "Gerar Diferença" já com o valor novo,
  // vindo do retorno do fetch (nunca do estado, que ainda está velho aqui).
  const afterContractedIncrease = async () => {
    const _total = await fetchFinancialData();
    if (!(_total?.diferenceToGenerate > 0.5)) {
      toast.info("Alteração salva. No momento não há diferença a gerar para este formando.");
      return;
    }
    setshowModalFinancial({
      open: true,
      transaction_config: { type: "gerar-diferenca", batch: false },
      pre_load: {
        trainee_ids: [trainee?.id],
        commercial_id: trainee?.commercial_id,
        value: _total.diferenceToGenerate,
      },
    });
  };

  const [showModalFinancial, setshowModalFinancial] = useState();
  const [showModalAdditionalItem, setshowModalAdditionalItem] = useState();
  const [showModalAdditionalInvite, setshowModalAdditionalInvite] = useState();
  const [showModalDiscountRemove, setshowModalDiscountRemove] = useState();

  // Registros desabilitados são soft-delete: o backend já os ignora
  // (CustomPaymentService/DiscountTraineeService filtram disabled=false), então
  // a tela precisa ignorar também, senão o resumo diverge do saldo do servidor.
  const activeAdditionalItems =
    financial?.aditional_items?.filter(__item => __item.payer_type === "item_adicional" && !__item.disabled) || [];
  const activeDiscounts = financial?.discounts?.filter(__discount => !__discount.disabled) || [];
  // Só faz sentido oferecer o atalho de Convites/Ingressos Extras quando algum evento do
  // formando tem Base configurada na planilha de custos — senão não há o que estender.
  const hasEventInviteEvents = [
    ...(financial?.package?.events || []),
    ...(financial?.singles?.map(__single => __single?.event) || []),
  ].some(__event => __event?.qtd_invitations != null || __event?.qtd_tickets != null);

  const [isLoadingSlips, setisLoadingSlips] = useState();
  useEffect(() => {
    setisWaitingResponse(isLoadingSlips);
  }, [isLoadingSlips, setisWaitingResponse]);

  const [isNewFinancial, setisNewFinancial] = useState();
  const hasContractedSlips = financial?.payment_slips?.contracted?.length > 0;
  useEffect(() => {
    setisNewFinancial(!hasContractedSlips);
  }, [hasContractedSlips]);

  const updateSlip = props => {
    const { slip_id, update } = props;
    setfinancial({
      ...financial,
      payment_slips: {
        ...financial?.payment_slips,
        contracted: financial?.payment_slips?.contracted?.map(__slip => {
          return __slip.id === slip_id
            ? {
                ...__slip,
                ...update,
              }
            : __slip;
        }),
      },
    });
  };

  const checkAllActives = value => {
    setfinancial({
      ...financial,
      payment_slips: {
        ...financial?.payment_slips,
        contracted: financial?.payment_slips?.contracted?.map(__slip => {
          return {
            ...__slip,
            ...(__slip?.status !== "Pago" && __slip?.status !== "Cancelado" ? { checked: value } : {}),
          };
        }),
      },
    });
  };

  const [allSlipsCancel, setallSlipsCancel] = useState();
  const [showAllCancel, setshowAllCancel] = useState();
  const [allCancelLoading, setallCancelLoading] = useState(false);
  useEffect(() => {
    if (!allSlipsCancel && !!showAllCancel) {
      (async () => {
        try {
          setallCancelLoading(true);
          let { data: _slips } = await api.get(
            `/paymentslips?trainee_id=${trainee?.id}&commercial_id=${trainee?.commercial_id}&status=cancelado&paginate=false`,
            config,
          );
          _slips = formatPaymentSlips(_slips?.filter(__slip => !__slip?.customPayments));
          setallSlipsCancel(_slips);
        } catch (__error) {
          console.log("__error", __error);
          toast.error("Problema ao carregar os pagamentos cancelados.");
        } finally {
          setallCancelLoading(false);
        }
      })();
    }
  }, [showAllCancel, allSlipsCancel, config, trainee?.id]);

  return (
    <Row>
      <Col sm={9}>
        <Main>
          {!isLoadingSlips ? (
            <>
              {!isNewFinancial ? (
                <Content>
                  {(() => {
                    const _active_slips = financial?.payment_slips?.contracted
                      ?.filter(__payment => __payment.status !== "Cancelado")
                      ?.filter(__payment => __payment.status !== "Pago");
                    return (
                      <>
                        <Row>
                          <Col>
                            <h2 className="mini-title">
                              {_active_slips?.length || 0} Pagamentos Ativos:{" "}
                              <span className="small-label">(Financeiro Contratado)</span>
                            </h2>
                            <p className="small-label mb-2">
                              Boletos com vencimento em finais de semana e feriados poderão ser pagos no próximo dia
                              útil sem multas e taxas.
                            </p>
                          </Col>
                        </Row>
                        <SlipsTable
                          date_end={financial?.commercial?.date_end}
                          slips={_active_slips}
                          updateSlip={__update => {
                            updateSlip({
                              slip_id: __update.slip_id,
                              update: __update.update,
                            });
                          }}
                          checkAllToogle={checkAllActives}
                          show_total={true}
                          reload={reloadFinancialData}
                          multi_edit={true}
                          trainee_due_date={trainee?.due_date}
                        />
                      </>
                    );
                  })()}
                  {(() => {
                    const _payed_slips = financial?.payment_slips?.contracted?.filter(
                      __payment => __payment.status === "Pago",
                    );
                    return (
                      <>
                        <Row className="mt-4">
                          <Col>
                            <h2 className="mini-title">{_payed_slips?.length || 0} Pagamentos Efetuados:</h2>
                            <p className="small-label mb-2">
                              Histórico dos pagamentos realizados. Revise as transações para confirmar os valores
                              quitados.
                            </p>
                          </Col>
                        </Row>
                        <SlipsTable slips={_payed_slips} paid_values={true} show_total={true} />
                      </>
                    );
                  })()}
                  {(() => {
                    // Reembolsos são Títulos de Pagamento (Contas a Pagar) com o formando
                    // como beneficiário. Aparecem logo abaixo dos pagamentos efetuados por
                    // serem a contrapartida deles: parte do que ele pagou está voltando.
                    const _refund_rows = (financial?.refunds || []).flatMap(__negotiation =>
                      (__negotiation.billToPays || []).map(__bill => ({
                        ...__bill,
                        negotiation_description: __negotiation.description,
                        total_quotas: (__negotiation.billToPays || []).length,
                      })),
                    );
                    if (!(_refund_rows.length > 0)) return null;

                    const _refund_total = _refund_rows.reduce((__acc, __row) => __acc + (__row.value || 0), 0);
                    const _refund_status = __row => {
                      if (__row.status !== "a-pagar") return __row.status === "pago" ? "Pago" : __row.status;
                      const _due = new Date(__row.due_date);
                      const _today = new Date();
                      _today.setHours(0, 0, 0, 0);
                      return _due < _today ? "Atrasado" : "A Vencer";
                    };

                    return (
                      <>
                        <Row className="mt-4">
                          <Col>
                            <h2 className="mini-title">
                              {_refund_rows.length} Reembolso{_refund_rows.length > 1 ? "s" : ""} ao Formando:{" "}
                              <span className="small-label">(Contas a Pagar)</span>
                            </h2>
                            <p className="small-label mb-2">
                              Valores que a empresa devolve ao formando — normalmente por renegociação para um pacote de
                              menor valor do que ele já havia pago. O pagamento é confirmado na tela de Contas a Pagar.
                            </p>
                          </Col>
                        </Row>
                        <TableResponsive
                          table={{
                            head: [
                              { data: "Parcela" },
                              { data: "Status" },
                              { data: "Vencimento" },
                              { data: "Valor" },
                              { data: "Descrição" },
                            ],
                            body: _refund_rows.map((__row, __i) => ({
                              data: [
                                { element: <span>{`${__row.quotas}/${__row.total_quotas}`}</span> },
                                { element: <StatusFlag status={_refund_status(__row)} /> },
                                { element: <span>{formatDate(__row.due_date, 7)}</span> },
                                { element: <span>{formatReal(__row.value)}</span> },
                                { element: <span>{__row.description || __row.negotiation_description || "-"}</span> },
                              ],
                              key: `refund-${__row.id || __i}`,
                            })),
                          }}
                        />
                        <SumBoxes
                          className="mt-2"
                          elements={[
                            {
                              label: "A Reembolsar",
                              value: formatReal(
                                _refund_rows
                                  .filter(__row => __row.status === "a-pagar")
                                  .reduce((__acc, __row) => __acc + (__row.value || 0), 0),
                              ),
                            },
                            {
                              label: "Já Reembolsado",
                              value: formatReal(
                                _refund_rows
                                  .filter(__row => __row.status === "pago")
                                  .reduce((__acc, __row) => __acc + (__row.value_paid || __row.value || 0), 0),
                              ),
                            },
                            { label: "Total", value: formatReal(_refund_total) },
                          ]}
                        />
                      </>
                    );
                  })()}
                  <div>
                    {(() => {
                      const _canceled_slips = !showAllCancel
                        ? financial?.payment_slips?.contracted?.filter(__payment => __payment.status === "Cancelado")
                        : allSlipsCancel || [];
                      return (
                        <>
                          <Row className="mt-4">
                            <Col>
                              <h2 className="mini-title">
                                {_canceled_slips?.length || 0} Pagamentos Cancelados {!showAllCancel ? "com Juros" : ""}
                                : <span className="small-label">(Financeiro Contratado)</span>
                              </h2>
                              <p className="small-label mb-2">
                                {!showAllCancel
                                  ? "Histórico dos pagamentos cancelados que possuem juros. Precisamos desses valores para calcular multas e juros não pagos."
                                  : "Histórico de todos os pagamentos cancelados do Financeiro Contratado."}
                              </p>
                              <p>
                                <SwitchCheckbox
                                  label="Mostrar Todos"
                                  checked={!!showAllCancel}
                                  onChange={__ev => {
                                    setshowAllCancel(__ev.target.checked);
                                  }}
                                />
                              </p>
                            </Col>
                          </Row>
                          {!allCancelLoading ? (
                            <SlipsTable
                              slips={_canceled_slips}
                              updateSlip={__update => {
                                updateSlip({
                                  slip_id: __update.slip_id,
                                  update: __update.update,
                                });
                              }}
                              is_cancel={true}
                              reload={reloadFinancialData}
                              show_total={true}
                            />
                          ) : (
                            <Loading />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </Content>
              ) : !trainee?.cancel ? (
                <Content>
                  <h2 className="mini-title">Criar Financeiro do Formando:</h2>
                  <p className="small-label mb-2 mt-1">
                    Esse formando não possui o seu financeiro criado. Clique no botão abaixo para criar sua primeira
                    negociação.
                  </p>
                  <Button
                    style={{ maxWidth: "200px" }}
                    color="primary"
                    onClick={() => {
                      setshowModalFinancial({
                        open: true,
                        first_financial: true,
                        transaction_config: {
                          type: "receita",
                          batch: false,
                        },
                        pre_load: {
                          trainee_ids: [trainee?.id],
                          commercial_id: trainee?.commercial_id,
                        },
                      });
                    }}
                    disabled={!canNegotiate}
                    title={!canNegotiate ? "Você não tem permissão para realizar essa ação." : undefined}
                    data-vertical="true"
                  >
                    Criar Financeiro
                  </Button>
                </Content>
              ) : (
                <Content>
                  <p className="mb-0">Nenhum pagamento registrado e o vínculo com esse formando está Encerrado.</p>
                  <p className="mb-0">
                    Caso deseje criar o financeiro desse formando será necessário restaurar seu vínculo primeiro.
                  </p>
                </Content>
              )}
            </>
          ) : (
            <Content>
              <Loading />
            </Content>
          )}
        </Main>
      </Col>
      <Col sm={3}>
        <TraineeSubMenu trainee={trainee} />
        {!isNewFinancial && (
          <>
            {hasFullBond && (canNegotiate || canReadFinancial) && (
              <>
                <SubContent className="mt-3">
                  <h2 className="mini-title">{canNegotiate ? "Atualizar Negociação:" : "Simulador de Negociação:"}</h2>
                  {canNegotiate && Math.abs(totalGenerated?.diferenceToGenerate) > 0.5 && (
                    <p className="small-label mb-2 mt-1">
                      <b>Atenção:</b> Houve mudanças no financeiro desse formando no valor de{" "}
                      <b>{formatReal(totalGenerated?.diferenceToGenerate || 0)}</b>. Você poderá renegociar todo o
                      financeiro
                      {totalGenerated?.diferenceToGenerate > 0.5 ? " ou gerar a diferença para o financeiro atual" : ""}
                      .
                    </p>
                  )}
                  <div className="divisor mt-3 mb-3"></div>
                  <Button
                    color="warning"
                    onClick={() => {
                      setshowModalFinancial({
                        open: true,
                        transaction_config: {
                          type: "receita",
                          batch: false,
                        },
                        pre_load: {
                          trainee_ids: [trainee?.id],
                          commercial_id: trainee?.commercial_id,
                        },
                      });
                    }}
                    data-vertical="true"
                  >
                    {canNegotiate ? "Renegociar Financeiro" : "Simular Financeiro"}
                  </Button>

                  {!canNegotiate && (
                    <p className="small-label mb-2 mt-1">
                      Você não tem permissão para renegociar o financeiro deste formando, mas pode montar uma simulação
                      para conferir os valores. Nenhuma alteração será salva.
                    </p>
                  )}
                  {canNegotiate && (
                    <p className="small-label mb-2 mt-1">
                      <b>Atenção:</b> Todos os pagamentos ativos e não pagos serão cancelados para que a nova negociação
                      seja efetuada. Os valores de multas e juros serão passados para essa nova negociação.{" "}
                      <u>
                        Os valores de descontos e taxas da <b>negociação</b> não serão mantidos.
                      </u>
                    </p>
                  )}
                  {canNegotiate && totalGenerated?.diferenceToGenerate > 0.5 && (
                    <>
                      <div className="divisor mt-3 mb-3"></div>
                      <Button
                        color="warning"
                        onClick={() => {
                          setshowModalFinancial({
                            open: true,
                            transaction_config: {
                              type: "gerar-diferenca",
                              batch: false,
                            },
                            pre_load: {
                              trainee_ids: [trainee?.id],
                              commercial_id: trainee?.commercial_id,
                              value: totalGenerated?.diferenceToGenerate,
                            },
                          });
                        }}
                        data-vertical="true"
                      >
                        Gerar Diferença
                      </Button>
                      <p className="small-label mb-2 mt-1">
                        <b>Atenção:</b> A diferença será adicionada ao financeiro atual. Sem interferência nos
                        pagamentos já negociados anteriormente.
                      </p>
                    </>
                  )}
                </SubContent>
                {(canNegotiate || canUpdateFinancial) && (
                  <SubContent className="mt-3">
                    <h2 className="mini-title">Ajustes no Contratado:</h2>
                    <p className="small-label mb-2 mt-1">
                      Estas ações <b>aumentam</b> o valor devido pelo formando e geram apenas a diferença, sem cancelar
                      os pagamentos já negociados. Para <b>reduzir</b> o valor, use Renegociar Financeiro.
                    </p>
                    {canNegotiate && (
                      <>
                        <div className="divisor mt-3 mb-3"></div>
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => setshowModalAdditionalItem(true)}
                          data-vertical="true"
                        >
                          Add Item Adicional
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          disabled={!hasEventInviteEvents}
                          title={
                            !hasEventInviteEvents
                              ? "Nenhum evento deste formando tem Base de Convites/Ingressos configurada."
                              : undefined
                          }
                          onClick={() => setshowModalAdditionalInvite(true)}
                          data-vertical="true"
                        >
                          Add Convites/Ingressos Extras
                        </Button>
                      </>
                    )}
                    {canUpdateFinancial && (
                      <>
                        <Button
                          color="error"
                          size="sm"
                          disabled={!(activeDiscounts.length > 0)}
                          title={
                            !(activeDiscounts.length > 0)
                              ? "Este formando não possui bônus ou promoções ativos."
                              : undefined
                          }
                          onClick={() => setshowModalDiscountRemove(true)}
                          data-vertical="true"
                        >
                          Remover Bônus ou Promoção
                        </Button>
                      </>
                    )}
                  </SubContent>
                )}
              </>
            )}
            {(hasFullBond || hasActiveDistrato) && (
              <SubContent className="mt-3">
                <h2 className="mini-title">Resumo Financeiro:</h2>
                <p className="small-label mb-2 mt-1">
                  Qualquer alteração nos contratados exigirá uma renegociação financeira.
                </p>
                <div className="divisor mt-3 mb-3"></div>
                {(() => {
                  const _calc = financial?.values_calc?.contracted;
                  const _items = activeAdditionalItems;

                  const _package_value = financial?.package?.value_per_trainee_original || 0;
                  const _singles_value =
                    financial?.singles?.reduce(
                      (__acc, __single) => __acc + (__single?.value_per_trainee_original || 0),
                      0,
                    ) || 0;
                  const _items_value = _items.reduce((__acc, __item) => __acc + (__item?.cost || 0), 0);
                  const _event_invites_value = _calc?.event_invites_value || 0;
                  // somado a partir das mesmas linhas exibidas acima, para que a
                  // conta na tela feche com o que o gestor está vendo
                  const _total_contracted = _package_value + _singles_value + _items_value + _event_invites_value;

                  const _discounts_value = activeDiscounts.reduce(
                    (__acc, __discount) => __acc + (__discount?.value || 0),
                    0,
                  );
                  const _contributions = _calc?.contributions_benefits || 0;
                  const _refunds = _calc?.refunds_to_trainee || 0;
                  const _payments_made = _calc?.payments_made || 0;
                  const _payments_adjusts = _calc?.payments_made_adjusts || 0;
                  const _fines = _calc?.slips_fines_fees || 0;

                  // mesma fórmula do modal de renegociação (modal-financial-receipt.jsx),
                  // para que as duas telas cheguem ao mesmo número pelo mesmo caminho
                  const _total_debito_raw =
                    _total_contracted -
                    _contributions -
                    _discounts_value -
                    _payments_made +
                    _refunds -
                    _payments_adjusts +
                    _fines;
                  // quando tudo se anula sobra resíduo de ponto flutuante
                  // (ex: -5e-13), que o formatReal exibiria como "-R$ 0,00"
                  const _total_debito = Math.abs(_total_debito_raw) < 0.005 ? 0 : _total_debito_raw;

                  // verde = soma ao que o formando deve, laranja = abate
                  // (mesma convenção do StatusFlag Recebimento/Pagamento)
                  const _signed = (__value, __sign) => (
                    <span style={{ color: __sign === "-" ? "var(--color-warning)" : "var(--color-success)" }}>
                      {__sign} {formatReal(Math.abs(__value || 0))}
                    </span>
                  );
                  // detalhes ficam dentro da própria linha do grupo, abaixo do nome —
                  // como linhas soltas eles perdiam o vínculo com o grupo
                  const _group = (__title, __details) => (
                    <span>
                      {__title}
                      {__details?.length > 0 &&
                        __details.map((__detail, __detail_i) => (
                          <span key={`detail-${__detail_i}`} className="small-label d-block">
                            {__detail}
                          </span>
                        ))}
                    </span>
                  );

                  return (
                    <Results
                      rows={[
                        ...(financial?.package
                          ? [
                              {
                                label: _group(
                                  `Pacote ${financial?.package?.name || ""}`,
                                  financial?.package?.events?.map(__event => __event.name) || [],
                                ),
                                value: _signed(_package_value, "+"),
                              },
                            ]
                          : []),
                        {
                          label: _group(
                            "Avulsos",
                            financial?.singles?.map(
                              __single =>
                                `${__single?.event?.name}: ${formatReal(__single?.value_per_trainee_original || 0)}`,
                            ) || [],
                          ),
                          value: _signed(_singles_value, "+"),
                        },
                        {
                          label: _group(
                            "Itens Adicionais",
                            _items.map(__item => `${__item?.quantity} ${__item.name}: ${formatReal(__item?.cost)}`),
                          ),
                          value: _signed(_items_value, "+"),
                        },
                        {
                          label: "Convites/Ingressos Extras",
                          // Pode ser negativo — formando que abre mão de parte/todo o Base de um
                          // evento gera um desconto aqui, não um acréscimo.
                          value: _signed(_event_invites_value, _event_invites_value >= 0 ? "+" : "-"),
                        },
                        {
                          label: <b>Total Contratado</b>,
                          value: <b>{formatReal(_total_contracted)}</b>,
                          type: "dark",
                        },
                        {
                          label: _group(
                            "Bônus e Promoções",
                            activeDiscounts.map(
                              __discount => `${__discount.description}: ${formatReal(__discount?.value || 0)}`,
                            ),
                          ),
                          value: _signed(_discounts_value, "-"),
                        },
                        { label: "Pagamentos Efetuados", value: _signed(_payments_made, "-") },
                        {
                          label: "Taxas e Descontos de Pagamentos",
                          value: _signed(_payments_adjusts, _payments_adjusts > 0 ? "-" : "+"),
                        },
                        {
                          label: "Res. Aportes e Retiradas",
                          value: _signed(_contributions, _contributions > 0 ? "-" : "+"),
                        },
                        { label: "Multas e Juros (Atrasos)", value: _signed(_fines, "+") },
                        ...(_refunds > 0 ? [{ label: "Reembolso ao Formando", value: _signed(_refunds, "+") }] : []),
                        {
                          label: <b>Total Débito</b>,
                          value: <b>{formatReal(_total_debito)}</b>,
                          type: "dark",
                        },
                        // aqui todo valor é monetário: o label pode quebrar, o valor não
                      ].map(__row => ({ ...__row, nowrap: true }))}
                    />
                  );
                })()}
              </SubContent>
            )}
          </>
        )}
        {/* fora do `!isNewFinancial`: um formando sem financeiro lançado também
            pode precisar de distrato, e antes a ação sumia junto com o resto da
            barra lateral */}
        <TraineeEndBond trainee={trainee} reloadTraineeData={reloadTraineeData} />
      </Col>
      <ModalFinancialReceipt
        show={showModalFinancial?.open}
        onHide={() => {
          setshowModalFinancial();
        }}
        transaction_config={showModalFinancial?.transaction_config}
        pre_load={showModalFinancial?.pre_load}
        refreshCallback={() => {
          reloadFinancialData();
        }}
      />
      <ModalAdditionalItem
        show={!!showModalAdditionalItem}
        onHide={() => {
          setshowModalAdditionalItem();
        }}
        trainee={trainee}
        events={financial?.commercial?.packages_data?.events}
        refreshCallback={afterContractedIncrease}
      />
      <ModalAdditionalInvite
        show={!!showModalAdditionalInvite}
        onHide={() => {
          setshowModalAdditionalInvite();
        }}
        trainee={trainee}
        financial={financial}
        refreshCallback={afterContractedIncrease}
      />
      <ModalDiscountRemove
        show={!!showModalDiscountRemove}
        onHide={() => {
          setshowModalDiscountRemove();
        }}
        discounts={activeDiscounts}
        refreshCallback={afterContractedIncrease}
      />
    </Row>
  );
}
